import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreator } from "../creator/CreatorContext";
import { getStudioCreator, listStudioAssets } from "../data/client";
import { isVisible } from "../data/gate";
import type { Asset, Creator } from "../data/types";
import { Button, Display, Panel, Sprite } from "../design/primitives";
import { art } from "../design/sprites";
import css from "./welcome.module.css";

/**
 * The creator fork — `specs/perks-session-routing/` FR-5, FR-6, FR-7.
 *
 * What `/` renders for an owner. Not a redirect target and not a once-a-session
 * interstitial: a request for a specific place is always honoured, so a deep
 * link never passes through here (FR-4a).
 *
 * Reported as a bug on 2026-08-31 — signed in as the owner there was no path to
 * Studio at all, because patron chrome carries no Studio entry by design
 * (FR-10) and this screen did not exist.
 */
export default function Welcome() {
  const { creator, me } = useCreator();
  const navigate = useNavigate();

  /*
   * The counts need the grant matrix, which `/creator` strips - it is the shape
   * of the paywall and patrons never receive it. The owner is entitled to
   * `/studio/creator`, so the fork reads it rather than the numbers being
   * authored (FR-7).
   */
  const [studioCreator, setStudioCreator] = useState<Creator | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([getStudioCreator(), listStudioAssets()])
      .then(([c, a]) => {
        if (!live) return;
        setStudioCreator(c);
        setAssets(a);
      })
      // The fork's job is to offer two doors. Losing the counts must not cost
      // the owner the way into Studio, so this degrades rather than fails.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  /** How many published assets each tier can actually reach. */
  const countByTier = useMemo(() => {
    if (!studioCreator) return new Map<string, number>();
    return new Map(
      studioCreator.tiers.map((tier) => [
        tier.id,
        assets.filter((a) => !a.deletedAt && isVisible(a, tier, studioCreator)).length,
      ])
    );
  }, [studioCreator, assets]);

  // FR-6: default to the lowest tier that actually unlocks something. Tiers
  // arrive ordered by price, so the first with a count is the cheapest.
  const defaultTier = useMemo(
    () => creator.tiers.find((t) => (countByTier.get(t.id) ?? 0) > 0) ?? creator.tiers[0],
    [creator.tiers, countByTier]
  );
  const activeTier = creator.tiers.find((t) => t.id === selected) ?? defaultTier;

  /** The cheapest tier that reaches nothing — the one that hits the gate. */
  const gatedTier = creator.tiers.find((t) => (countByTier.get(t.id) ?? 0) === 0);

  const firstName = (me?.name ?? "").trim().split(" ")[0];

  return (
    <main className={css.page}>
      <Panel className={css.card}>
        <Sprite src={art.crt} size={130} />

        <Display size={34} as="h1">
          {firstName ? `SIGNED IN AS ${firstName.toUpperCase()}` : "SIGNED IN"}
        </Display>

        <p className={css.body}>
          You own {creator.displayName || "this campaign"}, so you get both
          sides. Pick where to start — you can move between them whenever you
          like.
        </p>

        <Button
          variant="gold"
          size="state"
          style={{ width: "100%" }}
          onClick={() => navigate("/studio")}
        >
          OPEN STUDIO
        </Button>

        {creator.tiers.length > 0 && (
          <div className={css.well}>
            <span className={css.wellLabel}>
              OR LOOK AT THE LIBRARY THE WAY A PATRON DOES
            </span>

            <div className={css.tierRow} role="group" aria-label="Preview tier">
              {creator.tiers.map((tier) => {
                const active = tier.id === activeTier?.id;
                return (
                  <button
                    key={tier.id}
                    type="button"
                    aria-pressed={active}
                    className={`${css.tierButton} ${active ? css.tierButtonActive : ""}`}
                    onClick={() => setSelected(tier.id)}
                  >
                    <Sprite src={art.cassette} size={26} />
                    {tier.name}
                  </button>
                );
              })}
            </div>

            {/* Computed from the live library and the matrix, never authored. */}
            {studioCreator && activeTier && (
              <span className={css.wellCount}>
                {activeTier.name.toUpperCase()} SEES{" "}
                {countByTier.get(activeTier.id) ?? 0} ASSETS
                {gatedTier && ` · ${gatedTier.name.toUpperCase()} HITS THE UPGRADE GATE`}
              </span>
            )}

            <Button
              variant="secondary"
              size="rail"
              style={{ width: "100%" }}
              disabled={!activeTier}
              onClick={() => activeTier && navigate(`/tabs?as=${activeTier.id}`)}
            >
              VIEW AS {activeTier?.name.toUpperCase() ?? "PATRON"}
            </Button>
          </div>
        )}

        <p className={css.footnote}>DOWNLOADS ARE DISABLED WHILE PREVIEWING.</p>
      </Panel>
    </main>
  );
}
